import {Template} from "../../../components/template/template.jsx";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";
import IntegrationEventsService from "../../../services/reports/integration-events-service.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {subMonths} from "date-fns";

const initialFilter = ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    plateOrCpf: "",
    source: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    plateOrCpf: "Placa",
    source: "Origem",
};

const initialTableColumns = {
    capture_time: {label: "D/H de captura", visible: true},
    dt_event: {label: "D/H do evento", visible: true},
    plate_or_cpf: {label: "Placa ou CPF", visible: true},
    reasons: {label: "Natureza", visible: true},
    signature: {label: "Assinatura", visible: true},
    options: {label: "Opções", visible: true},
    observation: {label: "Observação", visible: true},
    source: {label: "Origem do evento", visible: true},
    has_passage: {label: "Passagem do Sentry", visible: true}
};

export default function IntegrationEvents() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [reportData, setReportData] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [tableColumns, setTableColumns] = useState(initialTableColumns);
    const buttonRef = useRef(null);
    const printComponentRef = useRef(null);
    const title = "Eventos de integrações";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            },
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                plateOrCpf: filters.plateOrCpf ?? "",
                source: filters.source?.map(i => i.value) ?? [],
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels);

            const payload = {
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                plateOrCpf: formattedFilters.plateOrCpf,
                "cb-event-extern": formattedFilters.source,
                filters: JSON.stringify(auditFilters),
            };

            const response = await IntegrationEventsService.apply(payload);
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar relatório", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter);
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        };
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                printable={true}
                                headerChange={true}
                                visibilityColumns={true}
                                columns={tableColumns}
                                setColumns={setTableColumns}
                                printComponentRef={printComponentRef}
                                tableColumns={tableColumns}
                                reportData={reportData}
                                data={reportData?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ?  "p-[0]" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            toggleFilters={toggleFilters}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            apply={apply}
                            clearSearch={clearSearch}
                        >
                            <Filters setFilters={setFilters} filters={filters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                            <NotAppliedContent reportData={reportData} />
                            <NoDataContent reportData={reportData} data={reportData?.length} />
                            <Content
                                reportData={reportData}
                                data={reportData?.length}
                                entity={headerSelected}
                                title={title}
                                collapsed={collapsed}
                                setIsLoading={setIsLoading}
                                printComponentRef={printComponentRef}
                                tableColumns={tableColumns}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}